class ApiService {
  constructor() {
    this.baseUrl = this.getBaseUrl();
    this.defaultHeaders = this.getDefaultHeaders();
  }

  getBaseUrl() {
    const htmlElement = document.querySelector("html");
    return htmlElement?.getAttribute("data-base-url") || '';
  }

  getDefaultHeaders() {
    const htmlElement = document.querySelector("html");
    const countryId = htmlElement?.getAttribute("data-country-id");
    const currency = htmlElement?.getAttribute("data-currency");
    const language = htmlElement?.getAttribute("data-language");
    const session = localStorage.getItem('shopify_session');

    const headers = new Headers({
      "x-user-country-id": countryId,
      "x-user-currency": currency,
      "x-user-customer-id": "3",
      "x-user-language": language
    });

    if (session) {
      headers.set("x-user-session", session);
    }

    return headers;
  }

  buildHeaders(additionalHeaders = {}) {
    const headers = new Headers(this.defaultHeaders);
    
    Object.entries(additionalHeaders).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        headers.set(key, value);
      }
    });

    return headers;
  }

  async makeRequest(endpoint, options = {}) {
    const {
      method = 'GET',
      headers: additionalHeaders = {},
      body = null,
      responseType = 'json'
    } = options;

    const headers = this.buildHeaders(additionalHeaders);
    
    const requestOptions = {
      method,
      headers,
      redirect: "follow"
    };

    if (body) {
      requestOptions.body = typeof body === 'object' ? JSON.stringify(body) : body;
      if (typeof body === 'object') {
        headers.set("Content-Type", "application/json");
      }
    }

    try {
      const response = await fetch(`${this.baseUrl}${endpoint}`, requestOptions);
      
      return await this.handleResponse(response, responseType);
    } catch (error) {
      console.error(`API Error for ${endpoint}:`, error);
      throw new ApiError(`Network error: ${error.message}`, 'NETWORK_ERROR');
    }
  }

  async handleResponse(response, responseType) {
    const result = {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      headers: response.headers
    };

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: response.statusText };
      }
      
      throw new ApiError(
        errorData.message || `HTTP Error: ${response.status}`,
        'HTTP_ERROR',
        response.status,
        errorData
      );
    }

    switch (responseType) {
      case 'json':
        result.data = await response.json();
        break;
      case 'blob':
        result.data = await response.blob();
        break;
      case 'text':
        result.data = await response.text();
        break;
      case 'none':
        result.data = null;
        break;
      default:
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          result.data = await response.json();
        } else if (contentType?.includes('application/pdf')) {
          result.data = await response.blob();
        } else {
          result.data = await response.text();
        }
    }

    return result;
  }

  async checkPdfAvailability(consumerUserProductId) {
    try {
      const endpoint = `/a/ext/api/v1/consumer/products/${consumerUserProductId}/pdfConfiguration`;
      const response = await this.makeRequest(endpoint, {
        method: 'HEAD',
        responseType: 'none'
      });
      return response.ok;
    } catch (error) {
      console.warn(`PDF availability check failed for product ${consumerUserProductId}:`, error);
      return false;
    }
  }

  async downloadPdfConfiguration(consumerUserProductId) {
    const endpoint = `/a/ext/api/v1/consumer/products/${consumerUserProductId}/pdfConfiguration`;
    return await this.makeRequest(endpoint, {
      method: 'GET',
      responseType: 'auto'
    });
  }

  async updateSavedProductStatus(consumerUserProductId, shopifyMetaobjectId) {
    const endpoint = `/a/ext/api/v1/consumer/products/${consumerUserProductId}/shopifyMetaobjectId/${shopifyMetaobjectId}/status`;
    return await this.makeRequest(endpoint, {
      method: 'PUT',
      responseType: 'json'
    });
  }

  async deleteSavedProduct(consumerUserProductId, shopifyMetaobjectId) {
    const endpoint = `/a/ext/api/v1/consumer/products/${consumerUserProductId}/shopifyMetaobjectId/${shopifyMetaobjectId}`;
    return await this.makeRequest(endpoint, {
      method: 'DELETE',
      responseType: 'json'
    });
  }

  async updateConsent(consentData) {
    const endpoint = `/a/ext/api/v1/consumer/FromShopify`;
    return await this.makeRequest(endpoint, {
      method: 'PUT',
      body: consentData,
      responseType: 'json'
    });
  }

  async getOrderReceipt(orderId) {
    const endpoint = `/a/ext/api/v1/consumer/orders/${orderId}/receipt_shopify`;
    return await this.makeRequest(endpoint, {
      method: 'GET',
      responseType: 'blob'
    });
  }

  async getProductConfigurator(sku) {
    const endpoint = `/a/ext/api/v1/customers/3/web/products/sku/${sku}/configurator`;
    return await this.makeRequest(endpoint, {
      method: 'GET',
      responseType: 'json'
    });
  }

  async getConsumerProduct(userProductId) {
    const endpoint = `/a/ext/api/v1/consumer/products/${userProductId}`;
    return await this.makeRequest(endpoint, {
      method: 'GET',
      responseType: 'json'
    });
  }

  async createProduct(sku, variantId = null) {
    const endpoint = variantId 
      ? `/a/ext/api/v1/consumer/products/fromShopify/sku/${sku}/variantId/${variantId}`
      : `/a/ext/api/v1/consumer/products/fromShopify/sku/${sku}`;
    return await this.makeRequest(endpoint, {
      method: 'POST',
      responseType: 'json'
    });
  }

  async createRelatedProduct(payload) {
    const endpoint = `/a/ext/api/v1/consumer/products/related/FromShopify`;
    return await this.makeRequest(endpoint, {
      method: 'POST',
      body: payload,
      responseType: 'json'
    });
  }

  async createKitBunchProducts(products) {
    const endpoint = `/a/ext/api/v1/consumer/products/kitBunch/fromShopify`;
    return await this.makeRequest(endpoint, {
      method: 'POST',
      body: products,
      responseType: 'json'
    });
  }
}

class ApiError extends Error {
  constructor(message, type, status = null, data = null) {
    super(message);
    this.name = 'ApiError';
    this.type = type;
    this.status = status;
    this.data = data;
  }
}

const apiService = new ApiService();

if (typeof module !== 'undefined' && module.exports) {
  module.exports = { apiService, ApiService, ApiError };
}

if (typeof window !== 'undefined') {
  window.apiService = apiService;
  window.ApiService = ApiService;
  window.ApiError = ApiError;
}